import React, { useEffect, useState } from 'react';
import { StyleSheet, css } from 'aphrodite';
import { motion } from 'framer-motion';
import {
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Avatar,
  Button,
  Alert
} from '@mui/material';
import {
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  ResponsiveContainer
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import DashboardLayout from '../../../../components/patient/layouts/DashboardLayout.js';
import {
  calculateAge,
  showToast
} from '../../../../utils/user/patient/helpers.js';
import { usePatientAuth } from '../../../../components/patient/hooks/usePatientAuth.js';
import { APP_URLS } from '../../../../../urls.js';
import { toast } from 'react-toastify';
import { checkPatientProfileCompletion } from '../../../../redux/middleware/user/patient/auth/profile_completion_status.js';

// Mock chart data
const chartData = [
  { name: 'Mon', value: 50 },
  { name: 'Tue', value: 100 },
  { name: 'Wed', value: 75 },
  { name: 'Thu', value: 125 },
  { name: 'Fri', value: 90 },
  { name: 'Sat', value: 110 },
  { name: 'Sun', value: 120 }
];

// Action items from the original dashboard
const actionItems = [
  {
    title: 'Upcoming Sessions',
    color: '#2783F7',
    items: ['Appointments', 'Consultations', 'Reminders']
  },
  {
    title: 'Shopping & Gifts',
    color: '#ff7043',
    items: ['My Cart', 'Gifts', 'Rewards']
  },
  {
    title: 'Nearby Services',
    color: '#5c6bc0',
    items: ['Doctor', 'Pharmacy', 'Hospital']
  },
  {
    title: 'Health & Wellness',
    color: '#81c784',
    items: ['Self-Care', 'Fitness', 'Learn']
  },
  {
    title: 'Emergency',
    color: '#ffca28',
    items: ['Ambulance', 'Urgent Care', 'First Aid']
  },
  {
    title: 'Updates & Alerts',
    color: '#ffb74d',
    items: ['Health News', 'Vaccination', 'Alerts']
  }
];

const PatientDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { credentials, patientData } = usePatientAuth();
  const { isComplete, incompleteFields, loading, error } = useSelector(
    (state) => state.patientProfileCompletion
  );

  useEffect(() => {
    if (credentials) {
      dispatch(
        checkPatientProfileCompletion(credentials, (intent, message) =>
          showToast(toast, intent, message)
        )
      );
    }
  }, [dispatch, credentials]);

  const handleCompleteProfile = () => {
    navigate(APP_URLS.patientProfileCompletion);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={css(styles.container)}
    >
      {/* Profile Completion Alert */}
      {loading
        ? (
          <p>Checking profile status...</p>
          )
        : !isComplete && incompleteFields.length > 0
            ? (
              <Alert
                severity='info'
                sx={{ mb: 2 }}
                action={
                  <Button
                    color='inherit'
                    size='small'
                    onClick={handleCompleteProfile}
                    className={css(styles.alertButton)}
                  >
                    Complete Now
                  </Button>
					}
              >
                Please complete your profile to access all features
              </Alert>
              )
            : null}
      <Grid container spacing={3}>
        {/* Left Column: Profile Overview */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} className={css(styles.profileCard)}>
            <Avatar
              className={css(styles.avatar)}
              src='/static/images/avatar/1.jpg'
            />
            <Typography variant='h6' className={css(styles.profileName)}>
              {patientData?.first_name} {patientData?.last_name}
            </Typography>
            <Typography className={css(styles.profileDetail)}>
              Age:{' '}
              {patientData?.date_of_birth
							  ? calculateAge(patientData.date_of_birth)
							  : 'N/A'}{' '}
              | Blood Type: {patientData?.blood_group || 'N/A'}
            </Typography>
            <Typography className={css(styles.profileDetail)}>
              Gender: {patientData?.gender || 'N/A'}
            </Typography>
          </Paper>

          {/* Medical Book Card */}
          <Paper elevation={3} className={css(styles.medicalBookCard)}>
            <div className={css(styles.medicalBookContent)}>
              <Typography variant='h6' className={css(styles.medicalBookTitle)}>
                My Medical Book
              </Typography>
              <Button
                variant='contained'
                className={css(styles.medicalBookButton)}
                onClick={() => navigate(APP_URLS.patientTreatments)}
              >
                Open Medical Records
              </Button>
            </div>
          </Paper>
        </Grid>

        {/* Right Column: Analytics & Cards */}
        <Grid item xs={12} md={8}>
          {/* Analytics Card */}
          <Paper elevation={3} className={css(styles.analyticsCard)}>
            <Typography variant='h6' className={css(styles.cardTitle)}>
              My Activity Analysis
            </Typography>
            <div className={css(styles.chartContainer)}>
              <ResponsiveContainer width='100%' height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray='3 3' />
                  <XAxis dataKey='name' />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey='value' fill='#2783F7' />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Paper>

          {/* Action Buttons */}
          <Grid container spacing={2} className={css(styles.actionGrid)}>
            {actionItems.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card className={css(styles.actionCard)}>
                  <CardContent>
                    <Typography
                    variant='h6'
                    style={{ color: item.color }}
                    className={css(styles.actionTitle)}
                  >
                    {item.title}
                  </Typography>
                    {item.items.map((action, idx) => (
                    <Button
                    key={idx}
                    variant='outlined'
                    fullWidth
                    className={css(styles.actionButton)}
                    style={{ borderColor: item.color, color: '#333' }}
                  >
                    {action}
                  </Button>
                  ))}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </motion.div>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: '1.5rem',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  alertButton: {
    backgroundColor: '#2783F7',
    color: 'white',
    '&:hover': {
      backgroundColor: '#1b6ed9'
    }
  },
  profileCard: {
    padding: '1.5rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '1.5rem',
    backgroundColor: '#FFFFFF'
  },
  avatar: {
    width: '100px',
    height: '100px',
    marginBottom: '1rem',
    border: '4px solid #2783F7'
  },
  profileName: {
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '0.5rem'
  },
  profileDetail: {
    color: '#555',
    marginBottom: '0.25rem'
  },
  medicalBookCard: {
    backgroundImage: 'linear-gradient(135deg, #e6f0fd 0%, #c5dcfa 100%)',
    padding: '1.5rem',
    height: '200px',
    display: 'flex',
    alignItems: 'flex-end'
  },
  medicalBookContent: {
    width: '100%',
    textAlign: 'center'
  },
  medicalBookTitle: {
    color: '#2783F7',
    fontWeight: 'bold',
    marginBottom: '1rem'
  },
  medicalBookButton: {
    backgroundColor: '#2783F7',
    '&:hover': {
      backgroundColor: '#1b6ed9'
    }
  },
  analyticsCard: {
    padding: '1.5rem',
    marginBottom: '1.5rem',
    backgroundColor: '#FFFFFF'
  },
  cardTitle: {
    color: '#2783F7',
    fontWeight: 'bold',
    marginBottom: '1rem'
  },
  chartContainer: {
    width: '100%',
    height: '300px'
  },
  actionGrid: {
    marginTop: '1rem'
  },
  actionCard: {
    height: '100%',
    backgroundColor: '#FFFFFF'
  },
  actionTitle: {
    fontWeight: 'bold',
    marginBottom: '0.5rem',
    fontSize: '1.1rem'
  },
  actionButton: {
    marginBottom: '0.5rem',
    textTransform: 'none'
  }
});

export default PatientDashboard;
